import { createActionGroup, emptyProps, props } from '@ngrx/store'

import {
  Conversation,
  ConversationApiError,
  ConversationOutcomeMultiple,
  ConversationThread,
  ContextItem,
  ContextData,
  ExecutionPlanAction,
  ActionAgent,
  Message,
  KeepThreadOutcomeResponse,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { AgentName } from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'

import { ConversationDocument, GenerateBusinessRuleContext } from './ai-conversation.dictionary'

import {
  AiDocumentContentType,
  ContextDocument,
  FileUploadConfig,
  ChatMessage,
  UploadContextFile,
} from '../types/ai.types'

export const ConversationActions = createActionGroup({
  source: 'Conversation',
  events: {
    createConversationApiResponse: props<{ conversation: Conversation }>(),
    createThreadApiResponse: props<{ thread: ConversationThread }>(),
    addThreadMessageApiResponse: props<{
      threadMessage: Message
      threadId: string
    }>(),
    patchMessageContextItemsApiResponse: props<{
      threadId: string
      messageId: string
      context: ContextData
    }>(),
    updateMessageContextData: props<{
      messageId: string
      contextData: ContextItem[]
    }>(),
    discardThreadClick: props<{ addDiscardMessage?: boolean; message?: string }>(),
    keepOutcomeClick: emptyProps(),
    discardOutcomeClick: emptyProps(),
    keepOutcomeSuccess: emptyProps(),
    discardOutcomeSuccess: emptyProps(),
    keepThreadApiResponse: props<{
      conversationId: string
      threadId: string
      agent: ActionAgent
      pendingChanges: KeepThreadOutcomeResponse['pendingChanges']
      finalize: boolean
    }>(),
    discardThreadApiResponse: props<{
      conversationId: string
      threadId: string
    }>(),
    recoverThreadAfterError: props<{
      conversationId: string
      threadId: string
    }>(),
    conversationClosedSuccess: props<{ conversationId: string }>(),
    conversationClosedFailure: props<{ conversationId: string }>(),
    clearActiveConversation: emptyProps(),
    selectDestination: props<{ agent: AgentName; actionId: string }>(),
    conversationStreamingStarted: props<{
      messageId: string
    }>(),
    updatePendingMessage: props<{
      message: ChatMessage
    }>(),
    conversationCreationCancelled: emptyProps(),
    cancelIntentUnderstandingClick: emptyProps(),
    defaultThreadMessageCancelled: emptyProps(),
    uploadedDocument: props<{ documentId: string }>(),
    loadFileUploadConfigSuccess: props<{ fileUploadConfig: FileUploadConfig }>(),
    fileSelectedFromMessage: props<{ files: File[] }>(),
    fileUploadSuccess: props<{ fileId: string; fileName: string }>(),
    fileUploadFailure: props<{ error: string }>(),

    // File upload actions
    uploadedFilesUpdated: props<{ files: UploadContextFile[] }>(),
    fileRemoveClick: props<{ id: string }>(),
    clearUploadedFiles: emptyProps(),

    threadMessageFetchSuccess: props<{ thread: ConversationThread }>(),
    updateThreadOutcome: props<{ threadId: string; outcome: ConversationOutcomeMultiple }>(),
    reloadMessageSuccess: props<{ message: Message; threadId: string }>(),
    documentContextOpened: props<{
      resourceType?: AiDocumentContentType
      useInCurrentThread?: boolean
    }>(),
    documentContextClosed: emptyProps(),
    documentSelected: props<{ document: ConversationDocument | null }>(),
    documentResourcesOpened: emptyProps(),
    documentResourcesClosed: emptyProps(),
    documentReady: props<{ documentId: string }>(),
    documentUploaded: props<{ documentId: string }>(),
    addDefaultThreadMessageStarted: emptyProps(),
    addDefaultThreadMessageSuccess: emptyProps(),
    addDefaultThreadMessageError: emptyProps(),
    processNavigated: props<{
      processResourceId: string | null
      workspaceId: string | null
      appId: string | null
    }>(),
    loadConversation: props<{ conversationId: string; processId: string }>(),
    loadConversationSuccess: props<{ conversation: Conversation }>(),
    loadConversationFailure: props<{ error: string }>(),

    conversationErrorOccurred: props<{ error: ConversationApiError }>(),
    newSessionRequested: emptyProps(),

    autoStartAssistantAction: props<{
      action: ExecutionPlanAction
      skipUserConfirmation: boolean
    }>(),
  },
})

export const ActionEditActions = createActionGroup({
  source: 'ActionEdit',
  events: {
    businessRuleUpdate: props<GenerateBusinessRuleContext>(),

    generateBusinessRuleCtaClick: emptyProps(),

    explainBusinessRuleCtaClick: emptyProps(),

    fixBusinessRuleWarningsCtaClick: emptyProps(),
  },
})

export const CtaComponentActions = createActionGroup({
  source: 'CtaComponent',
  events: {
    generateProcessCtaClick: emptyProps(),
    generateUiCtaClick: emptyProps(),
  },
})

export const TypeAreaActions = createActionGroup({
  source: 'TypeArea',
  events: {
    fileSelected: props<{ files: File[] }>(),
    promptSubmit: props<{ text: string; files?: File[] }>(),
  },
})

export const AiChatBubbleActions = createActionGroup({
  source: 'AiChatBubble',
  events: {
    copyToChatInputField: props<{ text: string }>(),
    documentClick: props<{ document: ContextDocument }>(),
    assignPagesToUserTasks: emptyProps(),
  },
})
